import type { Values } from '@clovyr/pollen/manifest';
import type { LocalSsoAdministrator, LocalSsoInvite, PasskeyCredential } from '@clovyr/pollen/user';
import { bytesToBase64URL } from '@clovyr/pollen/util/base64';

const LOCAL_SSO_PROVIDER = 'webauthn_clovyr';

export const CLOVYR_SSO_ENABLED = 'CLOVYR_SSO_ENABLED';
export const CLOVYR_SSO_ISSUER = 'CLOVYR_SSO_ISSUER';
export const CLOVYR_SSO_IDENTITY_GRANTS = 'CLOVYR_SSO_IDENTITY_GRANTS';
export const SIMPLE_SSO_ENABLED = 'SIMPLE_SSO_ENABLED';
export const SIMPLE_SSO_NO_LOGIN = 'SIMPLE_SSO_NO_LOGIN';
export const SIMPLE_SSO_NO_LOGIN_REDIRECT = 'SIMPLE_SSO_NO_LOGIN_REDIRECT';

interface LocalSsoIdentityGrant {
  version: 1;
  grantId: string;
  provider: typeof LOCAL_SSO_PROVIDER;
  subject: string;
  role: 'admin' | 'manager' | 'default';
  allowedAudiences: string[];
  metadata: {
    credentials: {
      credentialId: string;
      publicKeyCose: string;
      signCount: number;
      transports: string[];
      rpId: string;
    }[];
  };
}

export interface LocalSsoMember {
  inviteID: number;
  subject: string;
  role: 'manager' | 'default';
  passkeys: NonNullable<LocalSsoInvite['recipient']>['passkeys'];
}

function requireOrigin(value: string, name: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error(`invalid ${name}`);
  }
  const localDevelopment =
    url.protocol === 'http:' &&
    (url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '[::1]');
  if (url.origin === 'null' || (url.protocol !== 'https:' && !localDevelopment)) {
    throw new Error(`invalid ${name}`);
  }
  return url.origin;
}

function localSsoGrant(
  deploymentID: string,
  audience: string,
  passkeys: (LocalSsoAdministrator['passkeys'][number] & { subject?: string })[],
  role: 'admin' | 'manager' | 'default',
  grantID: string,
): LocalSsoIdentityGrant {
  const origin = requireOrigin(audience, 'AI Cloud audience');
  const subject = passkeys[0]?.subject;
  if (!subject || !passkeys.length || passkeys.some((passkey) => passkey.subject !== subject)) {
    throw new Error('add a passkey before launching AI Cloud');
  }

  return {
    version: 1,
    grantId: grantID,
    provider: LOCAL_SSO_PROVIDER,
    subject,
    role,
    allowedAudiences: [origin],
    metadata: {
      credentials: passkeys.map((passkey) => {
        if (!passkey.id || !passkey.publicKeyCose || !passkey.rpId || passkey.signCount < 0) {
          throw new Error('invalid passkey metadata');
        }
        return {
          credentialId: passkey.id,
          publicKeyCose: passkey.publicKeyCose,
          signCount: passkey.signCount,
          transports: passkey.transports,
          rpId: passkey.rpId,
        };
      }),
    },
  };
}

export function localSsoOwnerGrant(
  deploymentID: string,
  audience: string,
  passkeys: PasskeyCredential[],
): LocalSsoIdentityGrant {
  // This reserved ID identifies bootstrap ownership independently of app role.
  // Keep it stable: AnythingLLM persists its local account mapping by grantId.
  return localSsoGrant(deploymentID, audience, passkeys, 'admin', `clovyr:${deploymentID}:owner`);
}

function localSsoMemberGrant(
  deploymentID: string,
  audience: string,
  member: LocalSsoMember,
): LocalSsoIdentityGrant {
  return {
    version: 1,
    grantId: `clovyr:${deploymentID}:invite:${member.inviteID}`,
    provider: LOCAL_SSO_PROVIDER,
    subject: member.subject,
    role: member.role,
    allowedAudiences: [requireOrigin(audience, 'AI Cloud audience')],
    metadata: {
      credentials: member.passkeys.map((passkey) => ({
        credentialId: passkey.id,
        publicKeyCose: passkey.publicKeyCose,
        signCount: passkey.signCount,
        transports: passkey.transports,
        rpId: passkey.rpId,
      })),
    },
  };
}

export function addLocalSsoValues(
  values: Values,
  deploymentID: string,
  audience: string,
  issuer: string,
  passkeys: PasskeyCredential[],
  members: LocalSsoMember[] = [],
  administrators?: LocalSsoAdministrator[],
) {
  const audienceOrigin = requireOrigin(audience, 'AI Cloud audience');
  const issuerOrigin = requireOrigin(issuer, 'Clovyr SSO issuer');
  if (administrators) {
    const owners = administrators.filter((admin) => admin.isOwner);
    if (owners.length !== 1) throw new Error('expected exactly one instance owner');
    if (!owners[0].passkeys.length) {
      throw new Error('missing owner passkeys; cannot refresh app access');
    }
  }
  const adminSubjects = new Set(administrators?.map((admin) => admin.subject));
  const grants = [
    ...(administrators
      ? administrators
          .filter((admin) => admin.passkeys.length)
          .map((admin) =>
            localSsoGrant(
              deploymentID,
              audienceOrigin,
              admin.passkeys.map((key) => ({ ...key, subject: admin.subject })),
              'admin',
              admin.isOwner
                ? `clovyr:${deploymentID}:owner`
                : `clovyr:${deploymentID}:admin:${admin.subject}`,
            ),
          )
      : [localSsoOwnerGrant(deploymentID, audienceOrigin, passkeys)]),
    ...members
      .filter((member) => !adminSubjects.has(member.subject))
      .map((member) => localSsoMemberGrant(deploymentID, audienceOrigin, member)),
  ];

  values[CLOVYR_SSO_ENABLED] = '1';
  values[CLOVYR_SSO_ISSUER] = new URL('/auth/local-sso', issuerOrigin).toString();
  values[CLOVYR_SSO_IDENTITY_GRANTS] = bytesToBase64URL(
    new TextEncoder().encode(JSON.stringify(grants)),
  );
  values[SIMPLE_SSO_ENABLED] = '1';
  values[SIMPLE_SSO_NO_LOGIN] = '1';
  values[SIMPLE_SSO_NO_LOGIN_REDIRECT] = new URL('/_clovyr/sso/start', audienceOrigin).toString();
}
